import { Link, useParams } from 'react-router-dom';
import { businessDate, daysBetween, systemClock } from '@sakhtar/calendar';
import {
  V2Card,
  V2CardHeader,
  V2CardBody,
  V2Table,
  V2Badge,
  V2Button,
  V2EmptyState,
  Alert,
  Icon,
  type SakhtarIconName,
  type V2TableColumn,
  type V2BadgeTone,
  LoadingState,
  StatePanel,
} from '@sakhtar/design-system/react';
import { BarChart, formatChartAmount } from '../../lib/charts/index.js';
import { useApiGet } from '../../lib/useApiGet.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import { amountColumnHeader, amountUnitLabel, formatRialFa } from '../../lib/finance-display.js';
import { useTenantSettings } from '../../lib/tenantSettings.js';
import {
  directPurchaseOrderAllowed,
  purchaseRequestsVisible,
} from '../../lib/types/tenantSettings.js';
import type { MasterDataListResponse } from '../../lib/types/masterData.js';
import type {
  PurchaseRequestListResponse,
  PurchaseRequest,
  PurchaseOrderListResponse,
  PurchaseOrder,
} from '../../lib/types/procurement.js';
import { requestStatusOf, orderStatusOf } from '../../lib/types/procurement.js';
import { HubKpi, HubKpiGrid } from '../../components/hub/HubKpi.js';
import { compactAmount } from '../admin/homeFormat.js';
import './hub.css';
import { HubYarCard } from '../../shell/yar/HubYarCard.js';

const PROCUREMENT_LIST_CAP = 100;
/** `GET master-data/parties` clamps `limit` to 200 (apps/api/src/master-data/repos.ts). */
const SUPPLIER_PAGE_LIMIT = 200;
const REQUEST_PREVIEW_ROWS = 6;

const fa = (n: number) => n.toLocaleString('fa-IR');

/** `YYYY-MM` → Jalali "year/month" only, via the existing display boundary
 * (displayJalaliDate on the month's first day) — never a new calendar
 * package function; formatJalali is numeric-only by design (JL-2) and a
 * month-only variant would be a display decision taken outside it. */
function monthLabelFa(month: string): string {
  const full = displayJalaliDate(`${month}-01`);
  const [year, monthPart] = full.split('/');
  return year && monthPart ? `${year}/${monthPart}` : full;
}

/** BigInt-safe Rial sum of a set of already-real totalRial digit strings —
 * never Number(...) per row (AGENTS.md §8). */
function sumTotalRial(rows: readonly { readonly totalRial: string }[]): string {
  return rows.reduce((sum, row) => sum + BigInt(row.totalRial), 0n).toString();
}

interface Fraction {
  readonly num: bigint;
  readonly den: bigint;
}

/**
 * Estimated Rial amount of a purchase request: Σ quantity × estimatedUnitPrice
 * over the lines that carry a price. `quantity` is the backend's exact
 * rational string (`n` or `n/d`, rationalToString in packages/modules/
 * procurement/src/refs.ts), so the sum is kept as an exact BigInt fraction
 * and rounded once — the same rule the home dashboard's server-side
 * `purchaseRequestsAwaiting` estimate uses (ROUND(SUM(qty × price))).
 * Returns null when no line has an estimated price (no honest figure).
 */
function requestEstimate(request: PurchaseRequest): Fraction | null {
  let total: Fraction | null = null;
  for (const line of request.lines) {
    const price = line.estimatedUnitPriceRial;
    if (price === null || !/^\d+$/.test(price)) continue;
    const match = /^(\d+)(?:\/(\d+))?$/.exec(line.quantity);
    if (!match) continue;
    const den = BigInt(match[2] ?? '1');
    if (den === 0n) continue;
    const part: Fraction = { num: BigInt(match[1]!) * BigInt(price), den };
    total =
      total === null
        ? part
        : { num: total.num * part.den + part.num * total.den, den: total.den * part.den };
  }
  return total;
}

function addFraction(a: Fraction | null, b: Fraction | null): Fraction | null {
  if (a === null) return b;
  if (b === null) return a;
  return { num: a.num * b.den + b.num * a.den, den: a.den * b.den };
}

/** Round half up to an integer Rial digit string. */
function roundFraction(f: Fraction): string {
  return ((2n * f.num + f.den) / (2n * f.den)).toString();
}

/** Whole days from `today` to a business date; null if absent/malformed. */
function daysUntil(
  date: string | null,
  today: ReturnType<typeof systemClock.today>,
): number | null {
  if (date === null) return null;
  try {
    return daysBetween(today, businessDate(date));
  } catch {
    return null;
  }
}

const REQUEST_STATUS_TONE: Record<string, V2BadgeTone> = {
  draft: 'neutral',
  submitted: 'warn',
  approved: 'success',
  rejected: 'danger',
  cancelled: 'neutral',
};

const ORDER_STATUS_TONE: Record<string, V2BadgeTone> = {
  پیش‌نویس: 'neutral',
  ارسال‌شده: 'info',
  تأیید‌شده: 'brand',
  'دریافت جزئی': 'warn',
  'دریافت کامل': 'success',
  لغو‌شده: 'neutral',
};

const OPEN_ORDER_STATUSES = new Set(['submitted', 'approved', 'partially_received']);

interface FlowNode {
  readonly module: 'procurement' | 'inventory' | 'finance';
  readonly icon: SakhtarIconName;
  readonly title: string;
  readonly description: string;
}

/** Static explanation of where procurement authority ends (reference card
 * «مرز اختیارها») — copy only, no data and no capability claim. */
const FLOW_NODES: readonly FlowNode[] = [
  {
    module: 'procurement',
    icon: 'cart',
    title: 'خرید و تأمین',
    description: 'درخواست، تأیید، سفارش خرید و تعهد به تأمین‌کننده',
  },
  {
    module: 'inventory',
    icon: 'inventory',
    title: 'انبار',
    description: 'دریافت فیزیکی کالا و اثر آن بر موجودی',
  },
  {
    module: 'finance',
    icon: 'finance',
    title: 'مالی و حسابداری',
    description: 'پرداختنی، پرداخت و ثبت سند حسابداری',
  },
];

/**
 * UI_REFERENCE_SOURCE:
 * section: Sakhtar Cloud ERP v2.dc.html — frame `/t/kimia/purchasing` (module overview)
 * canonical pattern: MODULE_HUB (HubKpiGrid hero + 3 tinted tiles, tinted V2Card
 *   sections: requests table, open orders, monthly trend, «مرز اختیارها» flow)
 * desktop reference / mobile reference: same frame; tiles wrap at 160px, the
 *   flow card stacks at <=640px with the arrows turned down the flow
 * light/dark reference: tokens only (`[data-module]` → `--sk-mod-*`), no hex colors
 * state reference: ready · empty · permission_denied (403 hides the section,
 *   never a fake zero) · network_error
 * v2 status: aligned — with these documented deviations, each because the
 *   backend has no data for what the frame shows (never fabricated):
 *   - «در حال آماده‌سازی انتشار» badge + unpublished-module banner + dashed
 *     «نمونهٔ طراحی — غیرفعال» chip: no entitlement/feature-flag/publish state
 *     is read by this page, and the frame's own copy says the figures are
 *     design samples; shown only if real state ever exists (not built).
 *   - Requests table «درخواست‌کننده» and «گام تأیید» columns: the request list
 *     response (apps/api/src/procurement/requests.controller.ts
 *     `toRequestResponse`, lines 55-67) returns neither the requester nor an
 *     approval step; «تاریخ درخواست» (real `requestedDate`) replaces them.
 *     «مبلغ برآوردی» is derived exactly from the real lines (quantity ×
 *     estimatedUnitPriceRial) — «—» when no line is priced.
 *   - Module tabs/CTA are rendered by ProcurementModuleLayout, not repeated here.
 *   - The frame has no «تأییدها» / «وضعیت دریافت کالا» pages: they are not
 *     routes in this app (PROCUREMENT_TABS).
 *   - «تأمین‌کنندگان فعال» comes from GET master-data/parties?role=supplier&
 *     status=active (limit clamps at 200; its `total` is only the current page,
 *     party.controller.ts:140, so the count is items.length and shows «+» at
 *     the clamp). The tile is hidden on any error (e.g. 403 without
 *     master.party.read).
 *   - Kept beyond the frame (real, already-shipped content): open-orders table
 *     with late rows in danger tone (replaces the separate late-orders card;
 *     lateness is also the hot ticks of the «سفارش خرید باز» tile) and the
 *     monthly order trend chart. The request-status donut was dropped: the
 *     frame shows the same split as the hero tile's pending strip.
 *
 * Legacy citation (superseded for layout, still valid for data limits):
 * section: Sakhtar Procurement.dc.html §05 OVERVIEW + REPORTS
 * archetype: MODULE_HUB
 * status: KIT_CONFORMANT — no procurement overview/report endpoint exists,
 *   so KPIs and the monthly-trend widget are computed client-side from the
 *   real, already-scoped GET .../procurement/requests and .../orders lists
 *   (record-scope already restricts each list to what this principal may see
 *   — no "mine" vs "all" filter is possible client-side, because the list
 *   responses never carry the requester/owner principal id). "درخواست‌های
 *   پرداخت" and "مانده پرداختنی تأمین‌کنندگان" KPIs are omitted entirely: no
 *   payment-request domain exists anywhere in the backend (confirmed, no
 *   table/module/controller), and procurement's own contract never returns
 *   AP/payable data — that boundary is real, not a permission gate to fake.
 *
 * FE-P0 FE-PRC-1 (BE_HYGIENE until BE-1): both GET .../procurement/{requests,
 * orders} cap at 100 rows server-side (packages/modules/procurement's own
 * `Math.min(limit ?? 100, 100)`) — the open/late KPIs, totals and trend below
 * are all indicative against that capped list, never an official report. A
 * visible cap-honesty banner fires whenever either list actually hits the cap,
 * rather than silently under-reporting. All Rial sums use BigInt, never
 * Number(totalRial). After BE-1 lands (GET /procurement/reports/*), FE-PRC-2
 * switches these to the real report and removes the cap banner.
 */
export function ProcurementHubPage() {
  const { slug } = useParams<{ slug: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const settings = useTenantSettings();
  const allowDirectPo = directPurchaseOrderAllowed(settings);
  const requestsEnabled = purchaseRequestsVisible(settings);
  const { amountDisplayUnit } = settings;

  const requests = useApiGet<PurchaseRequestListResponse>(
    slug && requestsEnabled ? `${base}/api/v1/procurement/requests` : null,
    [slug, requestsEnabled],
  );
  const orders = useApiGet<PurchaseOrderListResponse>(
    slug ? `${base}/api/v1/procurement/orders` : null,
    [slug],
  );
  const suppliers = useApiGet<MasterDataListResponse>(
    slug
      ? `${base}/api/v1/master-data/parties?role=supplier&status=active&limit=${SUPPLIER_PAGE_LIMIT}`
      : null,
    [slug],
  );

  if (!slug) return null;

  const today = systemClock.today();
  const requestsCapped =
    requestsEnabled &&
    requests.status === 'ready' &&
    requests.data.requests.length === PROCUREMENT_LIST_CAP;
  const ordersCapped =
    orders.status === 'ready' && orders.data.orders.length === PROCUREMENT_LIST_CAP;

  const requestRows = requestsEnabled && requests.status === 'ready' ? requests.data.requests : [];
  const pendingRequests = requestRows.filter((r) => requestStatusOf(r.statusLabel) === 'submitted');
  const pendingEstimates = pendingRequests.map((r) => ({
    request: r,
    estimate: requestEstimate(r),
  }));
  const pendingEstimateTotal = pendingEstimates.reduce<Fraction | null>(
    (sum, p) => addFraction(sum, p.estimate),
    null,
  );
  const pendingEstimateRial = pendingEstimateTotal ? roundFraction(pendingEstimateTotal) : '0';
  // Hero strip: the three largest priced pending requests, sized by estimate.
  const heroTop = pendingEstimates
    .flatMap((p) =>
      p.estimate ? [{ ref: p.request.ref, rial: BigInt(roundFraction(p.estimate)) }] : [],
    )
    .filter((p) => p.rial > 0n)
    .sort((a, b) => (a.rial === b.rial ? 0 : a.rial > b.rial ? -1 : 1))
    .slice(0, 3);
  const heroTopTotal = heroTop.reduce((sum, p) => sum + p.rial, 0n);
  const heroWeights =
    heroTopTotal > 0n
      ? heroTop.map((p) => Math.max(1, Number((p.rial * 1000n) / heroTopTotal)))
      : undefined;

  const previewRequests = [...requestRows]
    .sort((a, b) => {
      const aPending = requestStatusOf(a.statusLabel) === 'submitted' ? 0 : 1;
      const bPending = requestStatusOf(b.statusLabel) === 'submitted' ? 0 : 1;
      if (aPending !== bPending) return aPending - bPending;
      return b.requestedDate.localeCompare(a.requestedDate);
    })
    .slice(0, REQUEST_PREVIEW_ROWS);

  const openOrders =
    orders.status === 'ready'
      ? orders.data.orders
          .filter((o) => OPEN_ORDER_STATUSES.has(orderStatusOf(o.statusLabel) ?? ''))
          .sort((a, b) => {
            if (a.expectedDate === b.expectedDate) return 0;
            if (a.expectedDate === null) return 1;
            if (b.expectedDate === null) return -1;
            return a.expectedDate.localeCompare(b.expectedDate);
          })
      : [];
  const openOrderTotalRial = sumTotalRial(openOrders);
  const lateOrders = openOrders.filter((o) => o.expectedDate !== null && o.expectedDate < today);
  const lateOrderTotalRial = sumTotalRial(lateOrders);
  const dueThisWeekCount = openOrders.filter((o) => {
    const days = daysUntil(o.expectedDate, today);
    return days !== null && days >= 0 && days <= 7;
  }).length;
  const partialOrderCount =
    orders.status === 'ready'
      ? orders.data.orders.filter((o) => orderStatusOf(o.statusLabel) === 'partially_received')
          .length
      : 0;

  // Supplier tile: items.length (the response `total` is the current page only).
  const supplierCount = suppliers.status === 'ready' ? suppliers.data.items.length : 0;
  const supplierCapped = supplierCount >= SUPPLIER_PAGE_LIMIT;
  const todayYear = displayJalaliDate(today).split('/')[0];
  const supplierNewThisYear =
    suppliers.status === 'ready'
      ? suppliers.data.items.filter((p) => {
          const created = p['createdAt'];
          return (
            typeof created === 'string' && displayJalaliDate(created).split('/')[0] === todayYear
          );
        }).length
      : 0;

  // Per-month bucket: BigInt-accumulated within the bucket, Number() applied
  // once per already-aggregated bucket at the chart-datapoint boundary —
  // never summed as Number across the raw rows first.
  const monthlyTotals = new Map<string, bigint>();
  if (orders.status === 'ready') {
    for (const o of orders.data.orders) {
      const month = o.orderDate.slice(0, 7);
      monthlyTotals.set(month, (monthlyTotals.get(month) ?? 0n) + BigInt(o.totalRial));
    }
  }
  const monthlyRows = [...monthlyTotals.entries()].sort((a, b) => a[0].localeCompare(b[0]));

  const requestColumns: readonly V2TableColumn<PurchaseRequest & { id: string }>[] = [
    {
      key: 'ref',
      header: 'شماره',
      render: (row) => <span className="ltrcell ltr sk-num">{row.ref}</span>,
    },
    { key: 'title', header: 'موضوع', render: (row) => row.title },
    {
      key: 'requestedDate',
      header: 'تاریخ درخواست',
      render: (row) => <span className="sk-num">{displayJalaliDate(row.requestedDate)}</span>,
    },
    {
      key: 'estimate',
      header: amountColumnHeader('مبلغ برآوردی', amountDisplayUnit),
      align: 'end',
      render: (row) => {
        const estimate = requestEstimate(row);
        return (
          <span className="sk-num">
            {estimate ? formatRialFa(roundFraction(estimate), amountDisplayUnit) : '—'}
          </span>
        );
      },
    },
    {
      key: 'status',
      header: 'وضعیت',
      render: (row) => (
        <V2Badge tone={REQUEST_STATUS_TONE[requestStatusOf(row.statusLabel) ?? ''] ?? 'neutral'}>
          {row.statusLabel}
        </V2Badge>
      ),
    },
  ];

  const orderColumns: readonly V2TableColumn<PurchaseOrder & { id: string }>[] = [
    {
      key: 'ref',
      header: 'شماره',
      render: (row) => <span className="ltrcell ltr sk-num">{row.ref}</span>,
    },
    { key: 'supplier', header: 'تأمین‌کننده', render: (row) => row.supplierPartyRef },
    {
      key: 'expected',
      header: 'سررسید مورد انتظار',
      render: (row) => {
        const late = row.expectedDate !== null && row.expectedDate < today;
        return (
          <span className="sk-num" style={late ? { color: 'var(--sk-danger)' } : undefined}>
            {displayJalaliDate(row.expectedDate)}
          </span>
        );
      },
    },
    {
      key: 'total',
      header: amountColumnHeader('مبلغ کل', amountDisplayUnit),
      align: 'end',
      render: (row) => (
        <span className="sk-num">{formatRialFa(row.totalRial, amountDisplayUnit)}</span>
      ),
    },
    {
      key: 'status',
      header: 'وضعیت',
      render: (row) => (
        <V2Badge tone={ORDER_STATUS_TONE[row.statusLabel] ?? 'neutral'}>{row.statusLabel}</V2Badge>
      ),
    },
  ];

  const requestsReady = requestsEnabled && requests.status === 'ready';
  const requestsHidden = requestsEnabled && requests.status === 'error';
  const heroScale =
    pendingEstimateRial !== '0'
      ? (() => {
          const amount = compactAmount(pendingEstimateRial, amountDisplayUnit);
          return `${amount.value} ${amount.scale}`;
        })()
      : undefined;
  const openOrdersScale =
    openOrders.length > 0
      ? (() => {
          const amount = compactAmount(openOrderTotalRial, amountDisplayUnit);
          return `${amount.value} ${amount.scale}`;
        })()
      : undefined;
  const lateFootParts = [
    lateOrders.length > 0 ? `${fa(lateOrders.length)} مورد دیرکرد` : null,
    dueThisWeekCount > 0 ? `${fa(dueThisWeekCount)} مورد سررسید این هفته` : null,
  ].filter((part): part is string => part !== null);

  const kpisReady =
    orders.status === 'ready' && (!requestsEnabled || requestsReady || requestsHidden);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <HubYarCard moduleId="procurement" />
      {orders.status === 'loading' || (requestsEnabled && requests.status === 'loading') ? (
        <LoadingState />
      ) : null}
      {requestsEnabled && requests.status === 'error' && requests.error.status !== 403 ? (
        <StatePanel
          status={requests.error.status}
          title="درخواست‌های خرید قابل نمایش نیست"
          onRetry={requests.reload}
        />
      ) : null}

      {requestsCapped || ordersCapped ? (
        <Alert tone="warning" title="داده‌های نمایه‌ای">
          نمایش محدود به ۱۰۰ ردیف — برای عدد قطعی گزارش سرور لازم است.
        </Alert>
      ) : null}

      {kpisReady && orders.status === 'ready' ? (
        <HubKpiGrid accent="procurement">
          {requestsReady ? (
            <HubKpi
              hero
              icon="cart"
              href={`${base}/procurement/requests`}
              label="درخواست در انتظار تأیید"
              value={fa(pendingRequests.length)}
              scale={heroScale}
              weights={heroWeights}
              foot={
                heroTop.length > 0 ? (
                  <span style={{ display: 'inline-flex', gap: 12 }}>
                    {heroTop.map((p) => (
                      <span key={p.ref} className="ltr sk-num">
                        {p.ref}
                      </span>
                    ))}
                  </span>
                ) : (
                  `از ${fa(requestRows.length)} درخواست`
                )
              }
            />
          ) : null}
          <HubKpi
            hero={!requestsReady}
            icon="invoice"
            href={`${base}/procurement/orders`}
            label="سفارش خرید باز"
            value={fa(openOrders.length)}
            scale={openOrdersScale}
            foot={
              lateFootParts.length > 0
                ? lateFootParts.join(' · ')
                : openOrders.length > 0
                  ? 'سررسید نزدیکی ندارد'
                  : 'سفارش بازی ثبت نشده'
            }
            footTone={lateOrders.length > 0 ? 'danger' : undefined}
            ticks={
              openOrders.length > 0
                ? { total: openOrders.length, hot: lateOrders.length }
                : undefined
            }
          />
          <HubKpi
            icon="box"
            href={`${base}/procurement/orders`}
            label="دریافت ناقص"
            value={fa(partialOrderCount)}
            foot={partialOrderCount > 0 ? 'پیگیری با تأمین‌کننده' : 'دریافت ناقصی نیست'}
          />
          {suppliers.status === 'ready' ? (
            <HubKpi
              icon="building"
              href={`${base}/procurement/suppliers`}
              label="تأمین‌کنندگان فعال"
              value={`${fa(supplierCount)}${supplierCapped ? '+' : ''}`}
              foot={
                supplierNewThisYear > 0
                  ? `${fa(supplierNewThisYear)} مورد جدید امسال`
                  : 'نقش «تأمین‌کننده» در اطلاعات پایه'
              }
            />
          ) : null}
        </HubKpiGrid>
      ) : null}

      {requestsReady ? (
        <V2Card>
          <V2CardHeader
            icon="invoice"
            title="درخواست‌های خرید"
            subtitle={`${fa(requestRows.length)} درخواست در دسترس شما`}
            actions={
              <>
                <Link to={`${base}/procurement/requests`}>
                  <V2Button variant="secondary" size="sm" icon="plus">
                    درخواست خرید
                  </V2Button>
                </Link>
                <Link to={`${base}/procurement/requests`}>
                  <V2Button variant="ghost" size="sm">
                    همه
                  </V2Button>
                </Link>
              </>
            }
          />
          <V2Table
            columns={requestColumns}
            rows={previewRequests.map((r) => ({ ...r, id: r.ref }))}
            zebra
            emptyState={<V2EmptyState icon="cart" compact title="درخواستی ثبت نشده" />}
          />
        </V2Card>
      ) : null}

      <V2Card>
        <V2CardHeader
          icon="cart"
          title="سفارش‌های خرید باز"
          subtitle={
            orders.status === 'ready'
              ? lateOrders.length > 0
                ? `${fa(lateOrders.length)} دیرکرد · مجموع ${formatChartAmount(lateOrderTotalRial, {
                    unit: amountDisplayUnit,
                    includeSuffix: true,
                  })}`
                : `${fa(openOrders.length)} سفارش باز`
              : undefined
          }
          actions={
            <Link to={`${base}/procurement/orders`}>
              <V2Button variant="ghost" size="sm">
                همه
              </V2Button>
            </Link>
          }
        />
        {orders.status === 'error' && orders.error.status !== 403 ? (
          <V2CardBody>
            <StatePanel
              status={orders.error.status}
              title="سفارش‌های خرید قابل نمایش نیست"
              onRetry={orders.reload}
            />
          </V2CardBody>
        ) : null}
        {orders.status === 'ready' ? (
          <V2Table
            columns={orderColumns}
            rows={openOrders.map((o) => ({ ...o, id: o.ref }))}
            zebra
            emptyState={
              <V2EmptyState
                icon="cart"
                title="سفارش خرید بازی ثبت نشده"
                action={
                  allowDirectPo ? (
                    <Link to={`${base}/procurement/orders`}>
                      <V2Button variant="secondary" size="sm">
                        سفارش خرید جدید
                      </V2Button>
                    </Link>
                  ) : undefined
                }
              />
            }
          />
        ) : null}
      </V2Card>

      {orders.status === 'ready' ? (
        <V2Card>
          <V2CardHeader
            icon="trend"
            title="روند خرید ماهانه"
            subtitle="نمایه‌ای، نه رسمی — از فهرست سفارش‌های در دسترس شما"
            actions={
              <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>
                مبلغ سفارش‌های ثبت‌شده · {amountUnitLabel(amountDisplayUnit)}
              </span>
            }
          />
          {monthlyRows.length === 0 ? (
            <V2EmptyState icon="cart" title="سفارشی ثبت نشده" />
          ) : (
            <div style={{ padding: '16px 20px' }}>
              <BarChart
                ariaLabel="روند خرید ماهانه"
                amountUnit={amountDisplayUnit}
                data={monthlyRows.map(([month, total]) => ({
                  label: monthLabelFa(month),
                  value: Number(total),
                }))}
              />
            </div>
          )}
        </V2Card>
      ) : null}

      <V2Card>
        <V2CardHeader icon="layers" title="مرز اختیارها" />
        <div className="proc-flow">
          {FLOW_NODES.map((node, index) => (
            <FlowStep key={node.module} node={node} withArrow={index > 0} />
          ))}
        </div>
      </V2Card>
    </div>
  );
}

function FlowStep({ node, withArrow }: { node: FlowNode; withArrow: boolean }) {
  return (
    <>
      {withArrow ? (
        <span className="proc-flow__arrow" aria-hidden="true">
          <span>
            <Icon name="chevronLeft" size="md" />
          </span>
        </span>
      ) : null}
      <div className="proc-flow__node" data-module={node.module}>
        <span className="proc-flow__chip">
          <Icon name={node.icon} size="sm" />
        </span>
        <div className="proc-flow__text">
          <div className="proc-flow__title">{node.title}</div>
          <div className="proc-flow__desc">{node.description}</div>
        </div>
      </div>
    </>
  );
}
